import { createPortal } from "react-dom";
import { Download, Share, X } from "lucide-react";
import { Icon, Modal, useCoarsePointer } from "@cntrl/ui";
import { canShareImage, saveImage, shareImage } from "./imageActions.js";
import styles from "./Inbox.module.scss";

export interface ImageLightboxProps {
  /** The photo's object URL — already fetched with the bearer by the bubble that opened this. */
  url: string;
  fileName: string;
  onClose: () => void;
}

/**
 * A chat photo, full screen (ART-267). Tapping a picture in a conversation opens it here, over everything, on a
 * dark ground, fitted to the screen.
 *
 * **Built on `Modal`, not hand-rolled**, so it keeps the focus trap, the Escape stack and the scroll lock — restyled
 * to bleed edge to edge, PORTALLED to <body> (the chat is itself an overlay, and an ancestor with a transform
 * would otherwise become the containing block for a fixed backdrop) and lifted to the dialog layer above the inbox.
 *
 * **The actions follow the INPUT, the way the message actions do**: a finger gets Save Image and Share as buttons
 * along the bottom of the photo — they were behind a `…` that opened a sheet, and the PO cut the extra step ("having
 * the 3 dots to pull this up is unnecessary — just have these at the bottom of the image"); a mouse gets a Download
 * button. Where they go is `imageActions.ts`'s business — native Photos and share sheet in the iOS app, a download
 * and the Web Share API in a browser — and Share is not offered at all where nothing can share a file.
 */
export function ImageLightbox({ url, fileName, onClose }: ImageLightboxProps) {
  const touch = useCoarsePointer();
  const shareable = canShareImage();

  const close = (
    <button type="button" className={styles.lightboxBtn} aria-label="Close photo" onClick={onClose}>
      <Icon icon={X} size={20} />
    </button>
  );

  return createPortal(
    <>
      <Modal
        title={fileName}
        hideHeader
        bodyLayout="flush"
        onClose={onClose}
        className={styles.lightbox}
        backdropClassName={styles.lightboxBackdrop}
      >
        <div className={styles.lightboxBar}>
          {close}
          {!touch && (
            <button
              type="button"
              className={styles.lightboxBtn}
              aria-label="Download"
              title="Download"
              onClick={() => void saveImage(url, fileName)}
            >
              <Icon icon={Download} size={20} />
            </button>
          )}
        </div>
        <div className={styles.lightboxStage} data-actions={touch ? "" : undefined}>
          {/* A tap on the dark space around the photo closes it, as a photo viewer does (PO). A real button
              filling the stage BEHIND the picture — the Modal scrim's shape — so it is a control rather than a
              click handler on a div; out of the tab order, since ✕ and Escape already close it from the keyboard.
              The picture sits above it and is not a close target. */}
          <button type="button" className={styles.lightboxScrim} aria-label="Dismiss photo" tabIndex={-1} onClick={onClose} />
          <img className={styles.lightboxImage} src={url} alt={fileName} />
        </div>
        {touch && (
          <div className={styles.lightboxActions}>
            <button type="button" className={styles.lightboxAction} onClick={() => void saveImage(url, fileName)}>
              <Icon icon={Download} size={20} />
              Save Image
            </button>
            {shareable && (
              <button type="button" className={styles.lightboxAction} onClick={() => void shareImage(url, fileName)}>
                <Icon icon={Share} size={20} />
                Share
              </button>
            )}
          </div>
        )}
      </Modal>
    </>,
    document.body,
  );
}
